import React, { useState, useRef, useCallback } from "react";
import "./ConfigGenerator.css";

const IMAGE_SIZE = 300;
const STORAGE_KEY = "configGenerator:lastJson";

const STORE_PRESETS = [
  "Stream on Spotify",
  "Stream on Deezer",
  "Stream on Apple Music",
  "Stream on YouTube Music",
  "Stream on Tidal",
  "Free Download",
];

let uid = 0;
const nextId = () => `id-${Date.now()}-${uid++}`;

function emptyGate() {
  return { id: nextId(), action: "", link: "" };
}

function emptyLink() {
  return {
    id: nextId(),
    title: "",
    url: "",
    hasStartDate: false,
    startDate: "",
    hasGates: false,
    gates: [emptyGate()],
  };
}

/**
 * Resizes/crops an uploaded image to a square IMAGE_SIZE x IMAGE_SIZE
 * PNG and returns it as a base64 data URL, matching the "product.image"
 * field used across the release JSON files.
 */
function fileToSquareBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("Could not read the file."));
    reader.onload = () => {
      const img = new Image();
      img.onerror = () => reject(new Error("Could not load the image."));
      img.onload = () => {
        const canvas = document.createElement("canvas");
        canvas.width = IMAGE_SIZE;
        canvas.height = IMAGE_SIZE;
        const ctx = canvas.getContext("2d");

        // cover-fit crop, centered
        const scale = Math.max(IMAGE_SIZE / img.width, IMAGE_SIZE / img.height);
        const drawWidth = img.width * scale;
        const drawHeight = img.height * scale;
        const dx = (IMAGE_SIZE - drawWidth) / 2;
        const dy = (IMAGE_SIZE - drawHeight) / 2;

        ctx.drawImage(img, dx, dy, drawWidth, drawHeight);
        resolve(canvas.toDataURL("image/png"));
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  });
}

function slugify(text) {
  return (
    (text || "release")
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/(^-|-$)/g, "") || "release"
  );
}

/**
 * Converts form state into the release JSON shape:
 * { links: [{ title, url, startDate?, gates? }], product: { image, title } }
 */
function buildData(title, image, links) {
  return {
    links: links
      .filter((l) => l.title.trim() || l.url.trim())
      .map((l) => {
        const entry = { title: l.title.trim(), url: l.url.trim() };
        if (l.hasStartDate && l.startDate) {
          entry.startDate = l.startDate;
        }
        if (l.hasGates) {
          const gates = l.gates
            .filter((g) => g.action.trim() || g.link.trim())
            .map((g) => ({ action: g.action.trim(), link: g.link.trim() }));
          if (gates.length) entry.gates = gates;
        }
        return entry;
      }),
    product: {
      image,
      title: title.trim(),
    },
  };
}

/**
 * Reverse of buildData: takes a raw JSON string (in the release JSON shape)
 * and turns it into form state, so an existing config can be re-opened
 * and edited.
 */
function parseConfigJson(raw) {
  let data;
  try {
    data = JSON.parse(raw);
  } catch (err) {
    throw new Error("That's not valid JSON: " + err.message);
  }
  if (!data || typeof data !== "object" || Array.isArray(data)) {
    throw new Error("The JSON must be an object with \"links\" and \"product\".");
  }

  const product = data.product || {};
  const rawLinks = Array.isArray(data.links) ? data.links : [];

  const links = rawLinks.map((l) => {
    const hasGates = Array.isArray(l.gates) && l.gates.length > 0;
    return {
      id: nextId(),
      title: typeof l.title === "string" ? l.title : "",
      url: typeof l.url === "string" ? l.url : "",
      hasStartDate: typeof l.startDate === "string" && l.startDate.length > 0,
      startDate: typeof l.startDate === "string" ? l.startDate : "",
      hasGates,
      gates: hasGates
        ? l.gates.map((g) => ({
            id: nextId(),
            action: typeof g.action === "string" ? g.action : "",
            link: typeof g.link === "string" ? g.link : "",
          }))
        : [emptyGate()],
    };
  });

  return {
    title: typeof product.title === "string" ? product.title : "",
    image: typeof product.image === "string" ? product.image : "",
    links: links.length ? links : [emptyLink()],
  };
}

function readSavedJson() {
  try {
    return window.localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

export default function ConfigGenerator({ onGenerate }) {
  const [title, setTitle] = useState("");
  const [image, setImage] = useState(""); // base64 data URL
  const [imageError, setImageError] = useState("");
  const [links, setLinks] = useState([emptyLink()]);
  const [jsonText, setJsonText] = useState("");
  const [copied, setCopied] = useState(false);
  const fileInputRef = useRef(null);

  const [jsonError, setJsonError] = useState("");
  const [hasSavedJson, setHasSavedJson] = useState(() => !!readSavedJson());

  const applyParsedConfig = (parsed) => {
    setTitle(parsed.title);
    setImage(parsed.image);
    setLinks(parsed.links);
    setImageError("");
  };

  // Parses whatever is currently in the JSON field and writes it into the
  // form fields — the reverse of "Generate JSON".
  const handleLoadFromJson = () => {
    try {
      const parsed = parseConfigJson(jsonText);
      applyParsedConfig(parsed);
      setJsonError("");
    } catch (err) {
      setJsonError(err.message || "Could not parse JSON.");
    }
  };

  const handleUploadFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onerror = () => setJsonError("Could not read the file.");
    reader.onload = () => {
      setJsonText(String(reader.result));
      setJsonError("");
    };
    reader.readAsText(file);
    e.target.value = ""; // allow re-uploading the same file later
  };

  const handleLoadSaved = () => {
    const raw = readSavedJson();
    if (!raw) return;
    setJsonText(raw);
    try {
      const parsed = parseConfigJson(raw);
      applyParsedConfig(parsed);
      setJsonError("");
    } catch (err) {
      setJsonError(err.message || "Could not parse the saved JSON.");
    }
  };

  const handleClearSaved = () => {
    try {
      window.localStorage.removeItem(STORAGE_KEY);
    } catch {
      // ignore
    }
    setHasSavedJson(false);
  };

  const handleImageChange = useCallback(async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImageError("");
    try {
      const base64 = await fileToSquareBase64(file);
      setImage(base64);
    } catch (err) {
      setImageError(err.message || "Invalid image.");
    }
  }, []);

  const updateLink = (id, patch) => {
    setLinks((prev) => prev.map((l) => (l.id === id ? { ...l, ...patch } : l)));
  };

  const addLink = () => setLinks((prev) => [...prev, emptyLink()]);

  const removeLink = (id) => setLinks((prev) => prev.filter((l) => l.id !== id));

  const moveLink = (id, direction) => {
    setLinks((prev) => {
      const index = prev.findIndex((l) => l.id === id);
      const newIndex = index + direction;
      if (newIndex < 0 || newIndex >= prev.length) return prev;
      const copy = [...prev];
      [copy[index], copy[newIndex]] = [copy[newIndex], copy[index]];
      return copy;
    });
  };

  const updateGate = (linkId, gateId, patch) => {
    setLinks((prev) =>
      prev.map((l) =>
        l.id !== linkId
          ? l
          : {
              ...l,
              gates: l.gates.map((g) => (g.id === gateId ? { ...g, ...patch } : g)),
            }
      )
    );
  };

  const addGate = (linkId) => {
    setLinks((prev) =>
      prev.map((l) => (l.id !== linkId ? l : { ...l, gates: [...l.gates, emptyGate()] }))
    );
  };

  const removeGate = (linkId, gateId) => {
    setLinks((prev) =>
      prev.map((l) =>
        l.id !== linkId ? l : { ...l, gates: l.gates.filter((g) => g.id !== gateId) }
      )
    );
  };

  const errors = [];
  if (!title.trim()) errors.push("Enter a title.");
  if (!image) errors.push("Upload an album cover.");
  if (!links.some((l) => l.title.trim() && l.url.trim())) {
    errors.push("Add at least one link with a title and URL.");
  }

  const handleGenerate = () => {
    if (errors.length) return;
    const data = buildData(title, image, links);
    const json = JSON.stringify(data, null, 2);
    setJsonText(json);
    setJsonError("");
    setCopied(false);
    try {
      window.localStorage.setItem(STORAGE_KEY, json);
      setHasSavedJson(true);
    } catch {
      // storage full or unavailable — the JSON is still shown/downloadable
    }
    if (onGenerate) onGenerate(data);
  };

  const handleCopy = async () => {
    if (!jsonText) return;
    await navigator.clipboard.writeText(jsonText);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  const handleDownload = () => {
    if (!jsonText) return;
    const blob = new Blob([jsonText], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${slugify(title)}.json`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="cg">
      <h1 className="cg-heading">Config generator</h1>
      <p className="cg-sub">
        Fill in the release details and generate the JSON on the right, or paste/edit
        JSON there and load it back into the form.
      </p>

      <div className="cg-layout">
        <div className="cg-form">
          <section className="cg-section">
            <label className="cg-label" htmlFor="cg-title">
              Title
            </label>
            <input
              id="cg-title"
              className="cg-input"
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Artist - Title"
            />
          </section>

          <section className="cg-section">
            <label className="cg-label" htmlFor="cg-image">
              Album cover ({IMAGE_SIZE}×{IMAGE_SIZE}px)
            </label>
            <div className="cg-image-row">
              <div className="cg-preview">
                {image ? (
                  <img src={image} alt="Cover preview" />
                ) : (
                  <span className="cg-preview-empty">No image</span>
                )}
              </div>
              <div>
                <input
                  id="cg-image"
                  ref={fileInputRef}
                  className="cg-file"
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                />
                <p className="cg-hint">
                  The image is automatically cropped and resized to {IMAGE_SIZE}×
                  {IMAGE_SIZE}px.
                </p>
                {imageError && <p className="cg-error">{imageError}</p>}
              </div>
            </div>
          </section>

          <section className="cg-section">
            <div className="cg-links-header">
              <span className="cg-label">Links</span>
              <button type="button" className="cg-btn cg-btn-ghost" onClick={addLink}>
                + Add link
              </button>
            </div>

            {links.map((link, index) => (
              <div className="cg-link-card" key={link.id}>
                <div className="cg-link-card-top">
                  <span className="cg-link-index">#{index + 1}</span>
                  <div className="cg-link-card-actions">
                    <button
                      type="button"
                      className="cg-icon-btn"
                      onClick={() => moveLink(link.id, -1)}
                      disabled={index === 0}
                      title="Move up"
                    >
                      ↑
                    </button>
                    <button
                      type="button"
                      className="cg-icon-btn"
                      onClick={() => moveLink(link.id, 1)}
                      disabled={index === links.length - 1}
                      title="Move down"
                    >
                      ↓
                    </button>
                    <button
                      type="button"
                      className="cg-icon-btn cg-icon-btn-danger"
                      onClick={() => removeLink(link.id)}
                      disabled={links.length === 1}
                      title="Remove"
                    >
                      ✕
                    </button>
                  </div>
                </div>

                <div className="cg-grid-2">
                  <div>
                    <label className="cg-label-sm">Title</label>
                    <input
                      className="cg-input"
                      list="cg-store-presets"
                      value={link.title}
                      onChange={(e) => updateLink(link.id, { title: e.target.value })}
                      placeholder="Stream on Spotify"
                    />
                  </div>
                  <div>
                    <label className="cg-label-sm">URL</label>
                    <input
                      className="cg-input"
                      type="url"
                      value={link.url}
                      onChange={(e) => updateLink(link.id, { url: e.target.value })}
                      placeholder="https://..."
                    />
                  </div>
                </div>

                <div className="cg-toggle-row">
                  <label className="cg-checkbox">
                    <input
                      type="checkbox"
                      checked={link.hasStartDate}
                      onChange={(e) =>
                        updateLink(link.id, { hasStartDate: e.target.checked })
                      }
                    />
                    Visible from date
                  </label>
                  {link.hasStartDate && (
                    <input
                      className="cg-input cg-input-date"
                      type="date"
                      value={link.startDate}
                      onChange={(e) => updateLink(link.id, { startDate: e.target.value })}
                    />
                  )}
                </div>

                <div className="cg-toggle-row">
                  <label className="cg-checkbox">
                    <input
                      type="checkbox"
                      checked={link.hasGates}
                      onChange={(e) => updateLink(link.id, { hasGates: e.target.checked })}
                    />
                    Unlock via action (gates)
                  </label>
                </div>

                {link.hasGates && (
                  <div className="cg-gates">
                    {link.gates.map((gate) => (
                      <div className="cg-gate-row" key={gate.id}>
                        <input
                          className="cg-input"
                          value={gate.action}
                          onChange={(e) =>
                            updateGate(link.id, gate.id, { action: e.target.value })
                          }
                          placeholder="Subscribe to YouTube Channel"
                        />
                        <input
                          className="cg-input"
                          type="url"
                          value={gate.link}
                          onChange={(e) =>
                            updateGate(link.id, gate.id, { link: e.target.value })
                          }
                          placeholder="https://..."
                        />
                        <button
                          type="button"
                          className="cg-icon-btn cg-icon-btn-danger"
                          onClick={() => removeGate(link.id, gate.id)}
                          disabled={link.gates.length === 1}
                          title="Remove action"
                        >
                          ✕
                        </button>
                      </div>
                    ))}
                    <button
                      type="button"
                      className="cg-btn cg-btn-ghost cg-btn-sm"
                      onClick={() => addGate(link.id)}
                    >
                      + Add action
                    </button>
                  </div>
                )}
              </div>
            ))}

            <datalist id="cg-store-presets">
              {STORE_PRESETS.map((p) => (
                <option key={p} value={p} />
              ))}
            </datalist>
          </section>

          {errors.length > 0 && (
            <ul className="cg-errors">
              {errors.map((err) => (
                <li key={err}>{err}</li>
              ))}
            </ul>
          )}

          <button
            type="button"
            className="cg-btn cg-btn-primary"
            onClick={handleGenerate}
            disabled={errors.length > 0}
          >
            Generate JSON
          </button>
        </div>

        <div className="cg-output">
          <div className="cg-output-header">
            <span className="cg-label">JSON</span>
            <div className="cg-output-actions">
              {hasSavedJson && (
                <button
                  type="button"
                  className="cg-btn cg-btn-ghost cg-btn-sm"
                  onClick={handleLoadSaved}
                >
                  Load last saved
                </button>
              )}
              {hasSavedJson && (
                <button
                  type="button"
                  className="cg-btn cg-btn-ghost cg-btn-sm"
                  onClick={handleClearSaved}
                >
                  Clear saved
                </button>
              )}
              <label className="cg-btn cg-btn-ghost cg-btn-sm cg-file-label">
                Upload .json
                <input
                  type="file"
                  accept="application/json,.json"
                  onChange={handleUploadFile}
                  className="cg-file-hidden"
                />
              </label>
              <button
                type="button"
                className="cg-btn cg-btn-ghost cg-btn-sm"
                onClick={handleCopy}
                disabled={!jsonText.trim()}
              >
                {copied ? "Copied!" : "Copy"}
              </button>
              <button
                type="button"
                className="cg-btn cg-btn-ghost cg-btn-sm"
                onClick={handleDownload}
                disabled={!jsonText.trim()}
              >
                Download
              </button>
            </div>
          </div>

          <textarea
            className="cg-json-textarea"
            spellCheck={false}
            value={jsonText}
            onChange={(e) => {
              setJsonText(e.target.value);
              if (jsonError) setJsonError("");
            }}
            onBlur={() => {
              if (jsonText.trim()) handleLoadFromJson();
            }}
            placeholder="Click &quot;Generate JSON&quot; to fill this in — or paste/edit an existing config JSON here. It loads into the fields above as soon as you click away."
          />

          {jsonError && <p className="cg-error">{jsonError}</p>}

          {hasSavedJson && (
            <p className="cg-storage-hint">
              The last generated JSON is saved in this browser's storage, so you can
              reload it later with "Load last saved".
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
